import type { CircleAiConversation } from "@circle-react/api/circleAiConversationsApi";
import type { MenuOptions } from "@circleco/compass/components/Menu";

import {
  type ProjectsState,
  useConversationActions,
} from "./ConversationActionsProvider";
import { ConversationCardMenu } from "./ConversationCardMenu";
import { useCopyCircleAiLink } from "./useCopyCircleAiLink";

type ActionableConversation = Pick<CircleAiConversation, "uuid" | "title"> & {
  isDailyBriefConversation: boolean;
  projectUuid?: string | null;
};

export const ConversationActionsTrigger = ({
  conversation,
}: {
  conversation: ActionableConversation;
}) => {
  const {
    copy,
    projectsState,
    projects,
    onMenuOpenChange,
    requestRename,
    requestDelete,
    addToProject,
    removeFromProject,
  } = useConversationActions();
  const copyLink = useCopyCircleAiLink();

  const selectableProjects = projects.filter(
    project => project.uuid !== conversation.projectUuid,
  );

  const transientSubmenu: Partial<Record<ProjectsState, MenuOptions>> = {
    loading: [{ label: copy.addToProject.loadingLabel, disabled: true }],
    error: [{ label: copy.addToProject.errorLabel, disabled: true }],
  };

  const submenu: MenuOptions =
    transientSubmenu[projectsState] ??
    (selectableProjects.length === 0
      ? [{ label: copy.addToProject.emptyLabel, disabled: true }]
      : selectableProjects.map(project => ({
          label: project.name,
          onClick: () =>
            addToProject(
              conversation.uuid,
              project,
              Boolean(conversation.projectUuid),
            ),
        })));

  const options: MenuOptions = [
    {
      icon: "pencil",
      label: copy.rename.menuLabel,
      onClick: () => requestRename(conversation),
    },
    {
      icon: "chain-link",
      label: copy.copyLink.menuLabel,
      onClick: () => copyLink(conversation.uuid),
    },
  ];

  if (!conversation.isDailyBriefConversation) {
    options.push({
      icon: "folder",
      label: conversation.projectUuid
        ? copy.addToProject.switchMenuLabel
        : copy.addToProject.menuLabel,
      submenu,
    });
  }

  if (conversation.projectUuid) {
    options.push({
      icon: "cross",
      label: copy.addToProject.removeMenuLabel,
      onClick: () => removeFromProject(conversation.uuid),
    });
  }

  options.push("divider");
  options.push({
    icon: "trash-can",
    label: copy.delete.menuLabel,
    danger: true,
    onClick: () => requestDelete(conversation),
  });

  return (
    <ConversationCardMenu options={options} onOpenChange={onMenuOpenChange} />
  );
};
